import { createFileRoute } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { preLoadDefaultProject } from '@/lib/queries/project';
import {
  getRCAByDemoIncidentId,
  mockDemoIncidents,
} from '@/lib/queries/rca/mockData';
import { useCreateUserFeedback } from '@/lib/queries/rca/feedback';

import { ChatInterface } from './-components/ChatInterface';
import { IncidentAlert } from './-components/IncidentAlert';
import { InvestigationTimeline } from './-components/InvestigationTimeline';
import { InvestigationFeedback } from './-components/InvestigationFeedback';
import {
  createMockMessage,
  getIncidentTitle,
  getMockAnnieResponse,
} from './-components/IncidentHelpers';

export const Route = createFileRoute('/_root/rca-demo/$incidentId/')({
  component: RouteComponent,
  beforeLoad: async ({ context, params }) => {
    await preLoadDefaultProject({ context });

    // Use mock demo incidents instead of real data
    const incident = mockDemoIncidents.find(
      inc => inc.id === params.incidentId,
    );

    const incidentTitle = getIncidentTitle(incident, params.incidentId);

    return {
      title: `${incidentTitle} - Demo RCA`,
    };
  },
  loader: async ({ context, params }) => {
    await preLoadDefaultProject({ context });

    // For demo, we don't need to load real incidents
    return { incidentId: params.incidentId };
  },
});

function RouteComponent() {
  const { incidentId } = Route.useLoaderData();
  const [chatMessages, setChatMessages] = useState<
    Array<{
      id: string;
      content: string;
      sender: 'user' | 'annie';
      timestamp: string;
    }>
  >([]);

  const createFeedbackMutation = useCreateUserFeedback();

  // Use mock demo incidents instead of real query
  const incident = mockDemoIncidents.find(inc => inc.id === incidentId);

  const handleNewMessage = (content: string) => {
    const newMessage = createMockMessage(content, 'user');
    setChatMessages(prev => [newMessage, ...prev]);

    setTimeout(() => {
      const annieResponse = createMockMessage(getMockAnnieResponse(), 'annie');
      setChatMessages(prev => [annieResponse, ...prev]);
    }, 1500);
  };

  const handleSubmitFeedback = async (feedback: {
    rating: 'positive' | 'negative';
    comment?: string;
  }) => {
    if (!rca) {
      return;
    }

    try {
      await createFeedbackMutation.mutateAsync({
        investigationId: rca.id,
        rating: feedback.rating,
        comment: feedback.comment,
      });
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    }
  };

  if (!incident) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Incident not found
        </h1>
        <Button variant="outline" onClick={() => window.history.back()}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go back
        </Button>
      </div>
    );
  }

  // Get RCA data by incident ID (works for all incident types)
  const rca = getRCAByDemoIncidentId(incident.id);

  return (
    <div className="min-h-full flex flex-col pb-24">
      {/* Header */}
      <div className="flex justify-center mb-6">
        <div className="flex items-center justify-between w-full max-w-7xl">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <FormattedMessage id="common.back" />
            </Button>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {getIncidentTitle(incident, incidentId)}
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex justify-center">
        <div className="flex gap-6 w-full max-w-7xl">
          {/* Left Side - Original Alert (Compact) */}
          <div className="w-80 flex-shrink-0">
            <div className="space-y-4">
              <IncidentAlert incident={incident} />
              {rca && (
                <InvestigationFeedback
                  existingFeedback={rca.userFeedback}
                  onSubmitFeedback={handleSubmitFeedback}
                />
              )}
            </div>
          </div>

          {/* Middle - Investigation Timeline */}
          <div className="flex-1 min-w-0">
            {rca ? (
              <InvestigationTimeline
                rca={rca}
                additionalChatMessages={chatMessages}
              />
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 text-center">
                <p className="text-gray-600 dark:text-gray-400">
                  <FormattedMessage id="rca.details.noInvestigation" />
                </p>
              </div>
            )}
          </div>

          {/* Right Side - Chat Interface */}
          <div className="w-96 flex-shrink-0">
            <div className="sticky top-6">
              <ChatInterface onSendMessage={handleNewMessage} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
